import { useCallback, useEffect, useRef } from "react";
import { Popout } from "./openPopout";

/** A window size — inner or outer, as the use site says. */
export type PopoutSize = {
  width: number;
  height: number;
};

/** Inner (content) dimensions, which are the units `openPopout` takes. */
export type RememberedPopoutSize = PopoutSize & {
  /** What this size was chosen to fit, so a stale one can be re-fitted. */
  fitsWidthPx: number;
  /** The height ask this size was chosen against, so a caller can spot a stale one. */
  fitsHeightPx: number;
};

/** Rounding slack, in pixels. */
export const SIZE_TOLERANCE = 4;

/** Within rounding of the wanted value. */
const near = (actual: number, wanted: number) =>
  Math.abs(actual - wanted) <= SIZE_TOLERANCE;

/** The window's own display, not the opener's once it has been moved. */
const displayOf = (win: Window) => win.screen ?? window.screen;

/** Identifies the display a window is on. A move to another one changes its scale. */
const displayKeyOf = (win: Window) => {
  const display = displayOf(win);
  return `${display.availWidth}x${display.availHeight}`;
};

/** The display's limit on an axis. A display reporting nothing usable caps nothing. */
const cappedBy = (limit: number, wanted: number) =>
  limit > 0 ? Math.min(wanted, limit) : wanted;

/** What the frame takes on each axis. Measured live: it grows when chrome appears. */
const frameOf = (win: Window): PopoutSize => ({
  width: Math.max(0, win.outerWidth - win.innerWidth),
  height: Math.max(0, win.outerHeight - win.innerHeight),
});

/** Outer dimensions leaving `inner` for the content, capped by the display. */
const outerFor = (win: Window, inner: PopoutSize): PopoutSize => {
  const display = displayOf(win);
  const frame = frameOf(win);
  return {
    width: cappedBy(display.availWidth, inner.width + frame.width),
    height: cappedBy(display.availHeight, inner.height + frame.height),
  };
};

/**
 * The largest content the display can show. A held size past it is never reached, so
 * `near` stops matching. Falls back to the opener's display, then to holding.
 */
const maxInnerSize = (win: Window): PopoutSize => {
  const display = displayOf(win);
  const frame = frameOf(win);
  const limit = (avail: number, fallback: number, taken: number) =>
    avail > 0 ? Math.max(0, avail - taken) : fallback;
  return {
    width: limit(
      display.availWidth || window.screen.availWidth,
      win.innerWidth,
      frame.width,
    ),
    height: limit(
      display.availHeight || window.screen.availHeight,
      win.innerHeight,
      frame.height,
    ),
  };
};

type Params<TStep extends string> = {
  popout: Popout | null;
  /** Sizes are remembered per step. Pass a constant for a single-step dialog. */
  step: TStep;
  /** Inner width the content wants right now. */
  targetWidthPx: number;
  /** Inner height the content wants, or 0 to leave it alone. Fitted whatever `enabled` says. */
  targetHeightPx?: number;
  /** False skips the width fit only; the size is still remembered and held against chrome. */
  enabled?: boolean;
};

/**
 * Fits a pop-out to the size its content needs without overriding one the user chose:
 * sizes are remembered per step, and a refit happens only when the need actually
 * changes. Without that second guard every render would discard the user's size.
 *
 * A first pop-out has no size to hold to, so chrome that bites before this runs keeps
 * its slice.
 */
export function usePopoutFit<TStep extends string>({
  popout,
  step,
  targetWidthPx,
  targetHeightPx = 0,
  enabled = true,
}: Params<TStep>) {
  const sizesRef = useRef<Partial<Record<TStep, RememberedPopoutSize>>>({});

  // A ref, not deps: re-subscribing the resize listener on a step change would
  // record a size before the window has been resized to suit it.
  const currentRef = useRef({ step, targetWidthPx, targetHeightPx });
  currentRef.current = { step, targetWidthPx, targetHeightPx };

  // What the current size fits, so the refit below acts only on a real change.
  const fittedForRef = useRef<{
    step: TStep;
    widthPx: number;
    heightPx: number;
  } | null>(null);

  // The inner size the window should be holding.
  const heldSizeRef = useRef<PopoutSize | null>(null);

  // The outer size last asked of the window. Both effects resize, so both record it.
  const askedOuterRef = useRef<PopoutSize>({ width: 0, height: 0 });

  // True from the ask until its resize arrives: a window landing wide of the ask
  // must not have the miss taken for a size the user chose.
  const awaitingAskRef = useRef(false);

  // The display the window is on, so a drag to another one is told from a resize.
  const displayRef = useRef("");

  // Only a resize the window took is an ask it can be held to.
  const askOuter = useCallback((win: Window, outer: PopoutSize) => {
    awaitingAskRef.current = true;
    try {
      win.resizeTo(outer.width, outer.height);
      askedOuterRef.current = outer;
      return true;
    } catch {
      awaitingAskRef.current = false;
      return false; // some window types refuse programmatic resizing
    }
  }, []);

  useEffect(() => {
    const win = popout?.win;
    if (!win) return;

    // Inner, not outer: outer would grow the window by its frame on every round trip.
    const remember = (ours: boolean) => {
      if (win.closed) return;
      const { innerWidth: width, innerHeight: height } = win;
      if (width <= 0 || height <= 0) return;

      // Only a size the user chose replaces the intent.
      const held = heldSizeRef.current;
      const size =
        held && (ours || (near(width, held.width) && near(height, held.height)))
          ? held
          : { width, height };

      const {
        step: forStep,
        targetWidthPx: fitsWidthPx,
        targetHeightPx: fitsHeightPx,
      } = currentRef.current;
      sizesRef.current[forStep] = { ...size, fitsWidthPx, fitsHeightPx };
      heldSizeRef.current = size;
    };

    /**
     * Puts the window back to the size it should hold; returns whether a resize was
     * issued. `window.open`'s features size the whole window and Chromium's popup strip
     * then takes ~40px of content, so a window opened at a remembered inner size doesn't
     * come back that size. `resizeTo` does.
     */
    const reassertSize = () => {
      const held = heldSizeRef.current;
      if (!held || win.closed) return false;
      if (
        near(win.innerWidth, held.width) &&
        near(win.innerHeight, held.height)
      ) {
        return false; // already near enough the size it should be
      }

      const outer = outerFor(win, held);
      const asked = askedOuterRef.current;
      if (near(outer.width, asked.width) && near(outer.height, asked.height)) {
        return false; // asked for this already; the window can't do better
      }
      return askOuter(win, outer);
    };

    /**
     * True when the window is still the outer size it was asked for while one axis of its
     * content changed — chrome taking a slice or handing one back. The user can't be the
     * cause: a drag, maximise or snap moves the window, and a zoom takes both axes.
     */
    const chromeChangedContent = () => {
      const held = heldSizeRef.current;
      if (!held) return false;
      const asked = askedOuterRef.current;
      if (
        !near(win.outerWidth, asked.width) ||
        !near(win.outerHeight, asked.height)
      ) {
        return false; // the window moved: the user's doing, not the browser's
      }
      return (
        (!near(win.innerWidth, held.width) &&
          near(win.innerHeight, held.height)) ||
        (!near(win.innerHeight, held.height) &&
          near(win.innerWidth, held.width))
      );
    };

    // Per axis, and only where it still fits what the content asks for: a stale axis
    // would be reasserted over the size the window just opened at.
    const { step: forStep, targetWidthPx, targetHeightPx } = currentRef.current;
    const last = sizesRef.current[forStep];
    heldSizeRef.current = last
      ? {
          width:
            last.fitsWidthPx === targetWidthPx ? last.width : win.innerWidth,
          height:
            last.fitsHeightPx === targetHeightPx
              ? last.height
              : win.innerHeight,
        }
      : null;

    askedOuterRef.current = { width: win.outerWidth, height: win.outerHeight };
    awaitingAskRef.current = false;
    displayRef.current = displayKeyOf(win);

    // Chrome has often bitten by the time this runs. A first pop-out has nothing to
    // reassert: record what it opened at.
    if (!reassertSize()) remember(false);

    const onResize = () => {
      const ours = awaitingAskRef.current;
      awaitingAskRef.current = false;

      // Displays scale against each other, so a drag between them resizes the content
      // without the user asking for anything. Recorded as their size it would ratchet
      // by that scale on every open.
      const display = displayKeyOf(win);
      if (display !== displayRef.current) {
        displayRef.current = display;
        askedOuterRef.current = {
          width: win.outerWidth,
          height: win.outerHeight,
        };
        reassertSize();
        return;
      }

      // The browser's slice never becomes the size to hold, even when the window
      // won't give it back.
      if (chromeChangedContent()) {
        reassertSize();
        return;
      }

      // The user's size is the one to hold from here.
      if (!ours) {
        askedOuterRef.current = {
          width: win.outerWidth,
          height: win.outerHeight,
        };
      }
      remember(ours);
    };

    win.addEventListener("resize", onResize);
    return () => win.removeEventListener("resize", onResize);
  }, [popout, askOuter]);

  useEffect(() => {
    const win = popout?.win;
    if (!win || win.closed) return;
    // Nothing to fit. Clearing stops another step's record reading as fitted on the
    // way back.
    if (!enabled && targetHeightPx <= 0) {
      fittedForRef.current = null;
      return;
    }

    // Don't override a size that already fits this state — it may be the user's.
    const fitted = fittedForRef.current;
    if (
      fitted &&
      fitted.step === step &&
      fitted.widthPx === targetWidthPx &&
      fitted.heightPx === targetHeightPx
    ) {
      return;
    }
    fittedForRef.current = {
      step,
      widthPx: targetWidthPx,
      heightPx: targetHeightPx,
    };

    // An axis with nothing to fit keeps what it was holding.
    const holding = heldSizeRef.current;

    // Targets are per-row asks and can far exceed the screen; held must stay reachable.
    const ceiling = maxInnerSize(win);
    const held = {
      width: enabled
        ? Math.min(targetWidthPx, ceiling.width)
        : (holding?.width ?? win.innerWidth),
      height:
        targetHeightPx > 0
          ? Math.min(targetHeightPx, ceiling.height)
          : (holding?.height ?? win.innerHeight),
    };
    heldSizeRef.current = held;

    const outer = outerFor(win, held);
    if (
      !near(win.outerWidth, outer.width) ||
      !near(win.outerHeight, outer.height)
    ) {
      askOuter(win, outer);
    }
  }, [popout, step, targetWidthPx, targetHeightPx, enabled, askOuter]);

  return {
    /**
     * Size to open at for this step, if one is remembered. Call at open time, not
     * render time — a resize doesn't re-render, so a render-time read can be stale.
     */
    rememberedSize: useCallback(
      (): RememberedPopoutSize | undefined => sizesRef.current[step],
      [step],
    ),
    /**
     * Record what the just-opened size fits, so the refit leaves it alone until
     * something changes. Pass a remembered size's `fitsWidthPx` when opening at one:
     * if it predates a change made while docked, the refit then kicks in.
     */
    noteOpened: useCallback(
      (fitsWidthPx: number, fitsHeightPx = 0) => {
        fittedForRef.current = {
          step,
          widthPx: fitsWidthPx,
          heightPx: fitsHeightPx,
        };
      },
      [step],
    ),
  };
}
